import { useTranslation } from 'react-i18next'
import {
  isLexSupportedSectionLocale,
  normalizeStandardSectionSourceLocale,
} from '@artem-lex/compliance-documents'
import {
  TRANSLATION_TARGET_LANGUAGES,
  type SupportedLanguage,
} from '@/shared/config/languages'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'

type ExtractTranslationLocalesFieldProps = {
  value: SupportedLanguage[]
  onChange: (locales: SupportedLanguage[]) => void
  sourceLocale?: string
  disabled?: boolean
}

export function ExtractTranslationLocalesField({
  value,
  onChange,
  sourceLocale,
  disabled = false,
}: ExtractTranslationLocalesFieldProps) {
  const { t } = useTranslation('standardsLibrary')
  const normalizedSourceLocale =
    normalizeStandardSectionSourceLocale(sourceLocale)

  const isLocaleBlockedBySource = (locale: SupportedLanguage): boolean =>
    normalizedSourceLocale != null &&
    isLexSupportedSectionLocale(normalizedSourceLocale) &&
    locale === normalizedSourceLocale

  const toggleLocale = (locale: SupportedLanguage, checked: boolean) => {
    if (isLocaleBlockedBySource(locale)) {
      return
    }
    if (checked) {
      if (!value.includes(locale)) {
        onChange([...value, locale])
      }
      return
    }
    onChange(value.filter((entry) => entry !== locale))
  }

  return (
    <div className='space-y-2'>
      <Label className='text-sm font-medium'>
        {t('detail.extractPreview.translationLocalesLabel')}
      </Label>
      <p className='text-sm text-muted-foreground'>
        {t('detail.extractPreview.translationLocalesDescription')}
      </p>
      <div className='flex flex-wrap gap-4'>
        {TRANSLATION_TARGET_LANGUAGES.map((lang) => {
          const checkboxId = `extract-translation-locale-${lang.value}`
          const localeBlocked = isLocaleBlockedBySource(lang.value)
          return (
            <div key={lang.value} className='flex items-center gap-2'>
              <Checkbox
                id={checkboxId}
                checked={value.includes(lang.value)}
                disabled={disabled || localeBlocked}
                onCheckedChange={(checked) => {
                  toggleLocale(lang.value, checked === true)
                }}
              />
              <Label
                htmlFor={checkboxId}
                className={
                  localeBlocked ? 'font-normal text-muted-foreground' : 'font-normal'
                }
              >
                <span className='mr-1'>{lang.flag}</span>
                {lang.label}
              </Label>
            </div>
          )
        })}
      </div>
    </div>
  )
}
